import { useI18n } from '@change-corgi/core/react/i18n';
import { Button } from '@change-corgi/design-system-legacy/components/actions';
import { Flex } from '@change-corgi/design-system-legacy/layout';
import { Text } from '@change-corgi/design-system-legacy/typography';

import type { PageRenderingButton, SupporterComment } from '../../shared/types';

import { useLikeComment } from './hooks/useLikeComment';
import { LikeIcon, UnLikeIcon } from './LikeCommentIcons';

type LikeCommentProps = {
	comment: SupporterComment;
	petitionId: string;
	renderedOn: PageRenderingButton;
};

export function LikeComment({ comment, petitionId, renderedOn }: LikeCommentProps): React.JSX.Element | null {
	const {
		data: { isCommentLiked, commentLikeCount, isGuest },
		actions: { handleLikeClick },
	} = useLikeComment({ comment, petitionId, renderedOn });

	const { translate } = useI18n();

	if (isGuest) {
		return null;
	}

	const ariaLabel = isCommentLiked
		? translate('corgi.components.petition_video.actions.aria_label.unlike_comment', {
				user_name: comment.user.shortDisplayName,
			})
		: translate('corgi.components.petition_video.actions.aria_label.like_comment', {
				user_name: comment.user.shortDisplayName,
			});

	return (
		<Button
			size="small"
			aria-label={ariaLabel}
			data-testid={`supporter-comment-${comment.id}-like-button-combo`}
			sx={{
				border: 'none',
				background: 'primary-greyBackground',
				// eslint-disable-next-line @typescript-eslint/naming-convention
				'&:hover': {
					background: 'rgba(0,0,0,0.1)',
				},
			}}
			onClick={handleLikeClick}
		>
			<Flex sx={{ alignItems: 'center' }}>
				{isCommentLiked ? <UnLikeIcon commentId={comment.id} /> : <LikeIcon commentId={comment.id} />}
				<Text
					fontWeight="bold"
					size="small"
					data-testid={`supporter-comment-${comment.id}-like-count`}
					ml={8}
					color={isCommentLiked ? 'primary-changeRed' : 'primary-black'}
				>
					{commentLikeCount}
				</Text>
			</Flex>
		</Button>
	);
}
